import SelectField from "@imminently/decisively-ui/SelectField";
import Select, { type AsyncOptionsLoader } from "@imminently/decisively-ui/Select";
import { contactsService } from "@/app/providers/services";
import { getContactDisplayName } from "@/lib/contact";
import type React from "react";

export type ContactSearchFieldProps = {
  /** Registers with an ancestor `Form` (RHF) instead of the controlled `value`/`onChange` pair. */
  name?: string;
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
};

const contactsAsyncLoader: AsyncOptionsLoader = {
  loader: async (inputValue) => {
    const result = await contactsService.getList({
      sort: { field: "lastModified", order: "desc" },
      pagination: { perPage: 20, page: 0 },
      ...(inputValue.trim() ? { searchName: inputValue.trim() } : {}),
    });
    return result.data.map((contact) => ({
      value: contact.id,
      label: getContactDisplayName(contact),
    }));
  },
  defaultOptions: true,
};

export const ContactSearchField = ({
  name,
  label,
  value,
  onChange,
  disabled,
  placeholder,
  required,
  fullWidth,
  helperText,
}: ContactSearchFieldProps) => (
  <SelectField
    name={name}
    label={label}
    // When `name` is set, leave value/onValueChange undefined so SelectField's
    // useField wires the value to the ancestor Form via RHF instead.
    value={name ? undefined : value || null}
    onValueChange={name ? undefined : (next) => onChange?.(next ?? "")}
    options={contactsAsyncLoader}
    searchable
    clearable
    disabled={disabled}
    placeholder={placeholder}
    required={required}
    fullWidth={fullWidth}
    helperText={helperText}
    renderMenuItem={(props) => (
      <Select.MenuItem {...props}>
        <div className="flex flex-col">
          <span className="text-sm">{props.data.label}</span>
          {props.data.label !== props.data.value ? (
            <span className="text-xs text-muted-foreground">{props.data.value}</span>
          ) : null}
        </div>
      </Select.MenuItem>
    )}
  />
);

export default ContactSearchField;
